import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { flushSync } from 'react-dom'
import { useSearchParams } from 'react-router-dom'
import { useAppStore } from '../../store/useAppStore'
import { useTelegramBackButton } from '../../hooks/useTelegramBackButton'
import RecipeOfTheDay from '../../components/RecipeOfTheDay/RecipeOfTheDay'
import { StoriesTray } from '@widgets/stories'
import SectionTabs from '../../components/SectionTabs/SectionTabs'
import SearchBar from '../../components/SearchBar/SearchBar'
import CategoryGrid from '../../components/CategoryGrid/CategoryGrid'
import DebugPanel from '../../components/DebugPanel/DebugPanel'
import SubscriptionBlock from '../../components/SubscriptionBlock/SubscriptionBlock'
import './HomePage.css'

const HomePage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { searchQuery, hasActiveSubscription, setSearchQuery } = useAppStore()

  // Флаг для предотвращения циклических обновлений
  const isUpdatingFromUrl = useRef(false)

  // Используем один state для всех уровней категорий
  const [categoryState, setCategoryState] = useState({
    section: searchParams.get('section') || '',
    subsection: searchParams.get('subsection') || '',
    subSubsection: searchParams.get('subSubsection') || '',
  })

  const [isFirstTabActive, setIsFirstTabActive] = useState(false)

  const hasSubscription = hasActiveSubscription()

  // Проверяем, есть ли активные фильтры (по URL, чтобы сработало сразу после возврата)
  const hasActiveFiltersFromUrl = !!(
    searchParams.get('section') ||
    searchParams.get('subsection') ||
    searchParams.get('subSubsection')
  )

  // Обработчик кнопки "Назад" для навигации по категориям
  const handleBackButtonClick = useCallback(() => {
    if (categoryState.subSubsection) {
      // Если выбрана подподкатегория, возвращаемся к подкатегории
      setCategoryState(prev => ({
        ...prev,
        subSubsection: '',
      }))
    } else if (categoryState.subsection) {
      // Если выбрана подкатегория, возвращаемся к категории
      setCategoryState(prev => ({
        ...prev,
        subsection: '',
      }))
    } else if (categoryState.section) {
      // Если выбрана категория, возвращаемся к главной
      setCategoryState({
        section: '',
        subsection: '',
        subSubsection: '',
      })
    }
  }, [categoryState])

  // Показываем кнопку "Назад" когда есть активные фильтры категорий (определяем по URL)
  useTelegramBackButton(hasActiveFiltersFromUrl, handleBackButtonClick)

  // Батчим обновления категорий
  const handleSectionChange = useCallback((section: string) => {
    setCategoryState({
      section,
      subsection: '',
      subSubsection: '',
    })
  }, [])

  const handleSubsectionChange = useCallback((subsection: string) => {
    setCategoryState(prev => ({
      ...prev,
      subsection,
      subSubsection: '',
    }))
  }, [])

  const handleSubSubsectionChange = useCallback((subSubsection: string) => {
    setCategoryState(prev => ({
      ...prev,
      subSubsection,
    }))
  }, [])

  // Синхронизируем состояние с URL параметрами
  useEffect(() => {
    if (isUpdatingFromUrl.current) {
      isUpdatingFromUrl.current = false
      return
    }

    const params = new URLSearchParams()

    if (categoryState.section) params.set('section', categoryState.section)
    if (categoryState.subsection)
      params.set('subsection', categoryState.subsection)
    if (categoryState.subSubsection)
      params.set('subSubsection', categoryState.subSubsection)

    const newParamsString = params.toString()
    const currentParamsString = searchParams.toString()

    if (newParamsString !== currentParamsString) {
      setSearchParams(params, { replace: true })
    }
  }, [categoryState, searchParams, setSearchParams])

  // Синхронизируем состояние при изменении URL
  useEffect(() => {
    const urlSection = searchParams.get('section') || ''
    const urlSubsection = searchParams.get('subsection') || ''
    const urlSubSubsection = searchParams.get('subSubsection') || ''

    if (
      urlSection !== categoryState.section ||
      urlSubsection !== categoryState.subsection ||
      urlSubSubsection !== categoryState.subSubsection
    ) {
      isUpdatingFromUrl.current = true
      setCategoryState({
        section: urlSection,
        subsection: urlSubsection,
        subSubsection: urlSubSubsection,
      })
    }
  }, [searchParams])

  const handleSearch = (query: string) => {
    setSearchQuery(query)
  }

  const handleFirstTabStatusChange = (isFirstTab: boolean) => {
    setIsFirstTabActive(isFirstTab)
  }

  // Мемоизируем текущую активную категорию
  const currentSection = useMemo(() => {
    return (
      categoryState.subSubsection ||
      categoryState.subsection ||
      categoryState.section
    )
  }, [categoryState])

  return (
    <div className="homepage">
      {/* Баннер "Рецепт дня" */}
      <RecipeOfTheDay hasSubscription={hasSubscription} />

      {/* Лента историй */}
      <StoriesTray />

      {/* Блок подписки - только для пользователей без подписки */}

      {/* Фильтры-табы - только для пользователей с подпиской */}
      {hasSubscription && (
        <SectionTabs
          activeSection={categoryState.section}
          onSectionChange={handleSectionChange}
          activeSubsection={categoryState.subsection}
          onSubsectionChange={handleSubsectionChange}
          activeSubSubsection={categoryState.subSubsection}
          onSubSubsectionChange={handleSubSubsectionChange}
          onFirstTabStatusChange={handleFirstTabStatusChange}
        />
      )}

      {/* Поиск - только для пользователей с подпиской */}
      {hasSubscription && (
        <SearchBar
          placeholder="Поиск по карточкам"
          onSearch={handleSearch}
          isFirstTabActive={isFirstTabActive}
        />
      )}

      {/* Сетка категорий: подписчикам — полная, без подписки — превью-карточки */}
      <CategoryGrid
        section={currentSection}
        searchQuery={searchQuery}
        onSectionChange={handleSectionChange}
        onSubsectionChange={handleSubsectionChange}
        onSubSubsectionChange={handleSubSubsectionChange}
      />
      {!hasSubscription && (
        <SubscriptionBlock hasSubscription={hasSubscription} />
      )}

      {/* <DebugPanel /> */}
    </div>
  )
}

export default HomePage
